import React, { useMemo } from "react";

import { useQuery } from "@apollo/client";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import calendarIcon from "@/assets/icons/calendar.png";
import pinnedClients from "@/components/Sidebar/ClickComponents/Client/store/PinnedClients";
import {
  sortByClientNameAsc,
  sortByPinnedPrimarily,
} from "@/components/Sidebar/ClickComponents/Client/utils/sort";
import { graphql } from "@/gql";
import useSearchText from "@/hooks/useSearchText";

import ClientRow, {
  DIVIDER_PADDING,
  DIVIDER_SIZE,
} from "./components/ClientRow";
import SearchPanel from "./components/SearchPanel";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: "20px",
  },
  title: {
    display: "flex",
    alignItems: "center",
    fontSize: 35,
    color: "#112340",
  },
  titleIcon: {
    width: 50,
    height: 50,
    marginRight: 15,
  },
  titleDate: {
    fontWeight: 700,
  },
  table: {
    height: "100%",
  },
  headCell: {
    borderBottom: 0,
    fontSize: 12,
    fontWeight: "600",
    color: "#112340",
    "& + &": {
      // To account for left border of subsequent columns.
      paddingLeft: DIVIDER_SIZE + DIVIDER_PADDING,
    },
  },
  headName: {
    display: "flex",
    alignItems: "center",
  },
  headNameIcon: {
    marginLeft: 9,
    color: "#7D8797",
  },
}));

const clientListPageQuery = graphql(`
  query ClientListPage {
    clients {
      nodes {
        ...ClientRow
        status # in order for the sort function to accept these nodes
      }
    }
  }
`);

const ClientListPage: React.FC = observer(() => {
  const { classes } = useStyles();
  const { data } = useQuery(clientListPageQuery);

  //search states
  const [searchText, setSearchText] = useSearchText({ debounce: 16 });

  const clients = useMemo(() => {
    return [...(data?.clients.nodes ?? [])]
      .filter((client) => {
        const search = searchText.toLowerCase();
        return client.profile?.fullName.toLowerCase().includes(search);
      })
      .sort(sortByPinnedPrimarily(sortByClientNameAsc));
  }, [data?.clients, searchText, pinnedClients.size]);

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>
        <img className={classes.titleIcon} src={calendarIcon} />
        <div>
          Today is{" "}
          <span className={classes.titleDate}>
            {format(new Date(), "MMMM d, yyyy")}
          </span>
        </div>
      </Typography>
      <SearchPanel onChange={(e) => setSearchText(e.target.value)} />
      <TableContainer>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell}>
                <div className={classes.headName}>
                  Name{" "}
                  <ArrowUpwardIcon
                    className={classes.headNameIcon}
                    fontSize="small"
                  />
                </div>
              </TableCell>
              <TableCell className={classes.headCell}>Status</TableCell>
              <TableCell className={classes.headCell}>Date Onboarded</TableCell>
              <TableCell className={classes.headCell}>
                Subscription Plan
              </TableCell>
              <TableCell className={classes.headCell}>
                Client Category
              </TableCell>
              <TableCell className={classes.headCell}>Month</TableCell>
              <TableCell className={classes.headCell}>
                Latest Timeline
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => (
              <ClientRow key={client.id} id={client.id} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});

export default ClientListPage;
